import { Html, useTexture } from "@react-three/drei"
import { useFrame } from "@react-three/fiber"
import { useEffect, useMemo, useRef } from "react"
import * as THREE from 'three'
import { useSceneRef } from "../../../RefsProvider"
import { useInterestPointStore } from "../../../store/uneInterestPointsStore"




export default function InterestPoint({ id, ...props }) {
    const refOvni = useSceneRef("ovni")
    const refPoint = useRef()
    const cubeRef = useRef()

    const mask = useTexture("./textures/04/mask.png")

    useEffect(() => {
        if (!mask) return
        mask.colorSpace = THREE.NoColorSpace
    }, [mask])

    const pointWorld = useMemo(() => new THREE.Vector3(), [])
    const ovniWorld = useMemo(() => new THREE.Vector3(), [])

    const radiusTrigger = 5
    const scaleMin = 1
    const scaleMax = 4

    const refCylMat = useRef()
    const refCubeMat = useRef()

    const colorActive = '#6558f6'

    const colorOff = new THREE.Color("#fafab9")
    const colorOn = new THREE.Color(colorActive)
    const cubeOff = new THREE.Color("#e68a63")
    const cubeOn = new THREE.Color(colorActive)

    const timeRef = useRef(0)
    const visited = useInterestPointStore(s =>
        id != null ? (s.points[id]?.visited ?? false) : false
    )

    const wasInsideRef = useRef(false)

    useFrame((_, delta) => {
        timeRef.current += delta

        if (!refPoint.current || !refOvni.current) return

        refPoint.current.getWorldPosition(pointWorld)

        const t = refOvni.current.translation()
        ovniWorld.set(t.x, t.y, t.z)

        const dx = pointWorld.x - ovniWorld.x
        const dz = pointWorld.z - ovniWorld.z
        const d2 = Math.hypot(dx, dz)

        const inside = d2 <= radiusTrigger
        const persistActive = inside || visited

        const targetScale = inside ? scaleMax : scaleMin
        refPoint.current.userData.circleScale = THREE.MathUtils.damp(
            refPoint.current.userData.circleScale ?? scaleMin,
            targetScale,
            5,   // <- moins rapide (essaie 4 à 7)
            delta
        )

        refPoint.current.userData.isActive = inside
        refPoint.current.userData.activeT = THREE.MathUtils.damp(
            refPoint.current.userData.activeT ?? 0,
            persistActive ? 1 : 0,
            8,
            delta
        )

        const k = refPoint.current.userData.activeT ?? 0

        if (refCylMat.current) {
            refCylMat.current.color.lerpColors(colorOff, colorOn, k)
            refCylMat.current.emissive.lerpColors(colorOff, colorOn, k)
        }

        if (refCubeMat.current) {
            refCubeMat.current.emissive.lerpColors(cubeOff, cubeOn, k)
        }

        cubeRef.current.position.y = 7 + Math.sin(timeRef.current * 1.5) * 0.25

        if (inside) {
            cubeRef.current.rotation.z += delta * 1.2

            cubeRef.current.rotation.y += delta * 1.2
        }


        const store = useInterestPointStore.getState()
        const currentId = store.activeInterestPointId

        if (inside) {
            wasInsideRef.current = true
            if (id != null && currentId !== id) {
                store.setActiveInterestPointId(id)
                store.markVisited(id)
            }
        } else {
            // on ne clear que si on vient de sortir
            if (wasInsideRef.current) {
                wasInsideRef.current = false
                if (currentId === id) store.setActiveInterestPointId(null)
            }
        }


    })

    return (
        <group ref={refPoint} {...props}>
            <mesh position={[0, 10, 0]} rotation={[Math.PI, 0, 0]}>
                <cylinderGeometry args={[0.5, 0.5, 20, 10, 1, true]} />
                <meshStandardMaterial
                    ref={refCylMat}
                    transparent
                    alphaMap={mask}
                    color="#fafab9"
                    emissive="#fafab9"
                    emissiveIntensity={1}
                />
            </mesh>

            <mesh ref={cubeRef} position={[0, 7, 0]} rotation={[0, 0, Math.PI * 0.25]}>
                <boxGeometry args={[0.4, 0.4, 0.4]} />
                <meshStandardMaterial ref={refCubeMat} emissive="#e68a63" emissiveIntensity={1} />
            </mesh>

            <CircleOutline
                isActive={refPoint.current?.userData?.isActive}
                parentRef={refPoint}
                rotation={[Math.PI * 0.5, 0, 0]}
                position={[0, 4, 0]}
            />
        </group>
    )
}

export function CircleOutline({ parentRef, isActive, ...props }) {
  const lineRef = useRef()
  const matRef = useRef()

  const geometry = useMemo(() => {
    const points = []
    const segments = 64
    const radius = 1
    for (let i = 0; i <= segments; i++) {
      const a = (i / segments) * Math.PI * 2
      points.push(new THREE.Vector3(Math.cos(a) * radius, Math.sin(a) * radius, 0))
    }
    return new THREE.BufferGeometry().setFromPoints(points)
  }, [])

  const easeInCubic = (t) => t * t * t
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3)

  const easeSplit = (t, split, easeInFn, easeOutFn) => {
    const s = THREE.MathUtils.clamp(split, 0.0001, 0.9999)

    if (t <= s) {
      const u = t / s
      return easeInFn(u) * s
    }

    const v = (t - s) / (1 - s) 
    return s + easeOutFn(v) * (1 - s)
  }

  const tRef = useRef(0)

  const scaleMin = 1
  const scaleMax = 4

  const durationIn = 2.0
  const durationOut = 0.4

  const easeInEnd = 0.76

  useFrame((_, delta) => {
    if (!lineRef.current) return

    const inside = !!parentRef?.current?.userData?.isActive

    const speed = inside
      ? (durationIn > 0 ? 1 / durationIn : 999)
      : (durationOut > 0 ? 1 / durationOut : 999)

    tRef.current = inside
      ? Math.min(1, tRef.current + delta * speed)
      : Math.max(0, tRef.current - delta * speed)

    const eased = easeSplit(tRef.current, easeInEnd, easeInCubic, easeOutCubic)

    const s = THREE.MathUtils.lerp(scaleMin, scaleMax, eased)
    lineRef.current.scale.set(s, s, s)

    const k = parentRef?.current?.userData?.activeT ?? 0
    if (matRef.current) {
      matRef.current.color.lerpColors(
        new THREE.Color("white"),
        new THREE.Color("#002fff"),
        k
      )
    }
  })

  return (
    <line ref={lineRef} geometry={geometry} {...props}>
      <lineBasicMaterial ref={matRef} color="white" />
    </line>
  )
}






